/**
 * Примитивы интерфейса по макетам docs/ux/voxel: плашка, кнопка, полоса, переключатель, плитка с подписью.
 * Стили — ./kit.css (подключается один раз при импорте kit). Корень экрана помечайте классом `kit`
 * (шрифт, цвет, тень текста).
 */
import type { ButtonHTMLAttributes, ComponentChildren, HTMLAttributes } from "preact";
import { Icon, type IconName } from "./icons";

type Div = HTMLAttributes<HTMLDivElement>;

/** Тёмная плашка со светлой рамкой. */
export function Plate({ class: cls, children, ...rest }: Div & { children?: ComponentChildren }) {
  return (
    <div class={cls ? `kit-plate ${String(cls)}` : "kit-plate"} {...rest}>
      {children}
    </div>
  );
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "icon"> {
  variant?: "gold" | "blue" | "dark";
  icon?: IconName;
  children?: ComponentChildren;
}

/** Кнопка: золотая — основное действие, синяя — второстепенное, тёмная — как плашка. */
export function Button({ variant = "gold", icon, class: cls, children, type = "button", ...rest }: ButtonProps) {
  const v = variant === "gold" ? "" : ` kit-btn--${variant}`;
  return (
    <button type={type} class={`kit-btn${v}${cls ? ` ${String(cls)}` : ""}`} {...rest}>
      {icon && <Icon name={icon} size={22} />}
      {children}
    </button>
  );
}

export interface BarProps {
  /** Заполнение, промилле (0…1000). */
  value: number;
  color?: "red" | "green" | "gold";
  class?: string;
  label?: string;
  testId?: string;
}

/** Полоса здоровья или прогресса. */
export function Bar({ value, color = "red", class: cls, label, testId }: BarProps) {
  const v = Math.max(0, Math.min(1000, value));
  return (
    <div
      class={`kit-bar${color === "red" ? "" : ` kit-bar--${color}`}${cls ? ` ${cls}` : ""}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={1000}
      aria-valuenow={v}
      data-testid={testId}
      style={{ "--kit-bar-fill": String(v / 1000) }}
    >
      <div class="kit-bar__fill" />
    </div>
  );
}

export interface ToggleProps {
  on: boolean;
  label: string;
  icon?: IconName;
  /** С замком: вместо переключателя замок, тап открывает предложение. */
  locked?: boolean;
  onChange(on: boolean): void;
  testId?: string;
  class?: string;
}

/** Переключатель на плашке с подписью («Автонавыки»). */
export function Toggle({ on, label, icon, locked, onChange, testId, class: cls }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      class={`kit-btn kit-btn--dark${cls ? ` ${cls}` : ""}`}
      data-testid={testId}
      data-locked={locked ? "1" : undefined}
      onClick={() => onChange(!on)}
    >
      {icon && <Icon name={icon} size={22} />}
      <span>{label}</span>
      {locked ? (
        <svg class="kit-lock" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 7V5a4 4 0 0 1 8 0v2h1v8H3V7zm2 0h4V5a2 2 0 0 0-4 0z" fill="#ffd23f" />
        </svg>
      ) : (
        <span class="kit-toggle" data-on={on ? "1" : "0"} />
      )}
    </button>
  );
}

export interface TileProps {
  icon: IconName;
  label: string;
  badge?: boolean;
  /** Подпись несёт значение (счётчик), поэтому видна и там, где у плиток только иконка. */
  value?: boolean;
  onClick(): void;
  testId?: string;
}

/** Квадратная кнопка-иконка с подписью и красной точкой «есть новое». */
export function Tile({ icon, label, badge, value, onClick, testId }: TileProps) {
  return (
    <button type="button" class="kit-tile" data-testid={testId} onClick={onClick}>
      <Icon name={icon} size={30} />
      <span class={value ? "kit-tile__label kit-tile__label--value" : "kit-tile__label"}>{label}</span>
      {badge && <span class="kit-badge" aria-hidden="true" />}
    </button>
  );
}
